import http from "services/httpService";
import { removeCommas, formatIndianNumber } from "utlis/utils";
import { object, string } from "yup";

const attributePresent = (attributes, name) => {
  const attribute = attributes.find(
    (attribute) => attribute.attribute_data.attribute_name === name
  );
  return attribute ? attribute.toggle : true;
};

export const validationSchema = (attributes, grade, isPostIssuance, selectedDependents = []) => {
  const validations = object({
    sum_insured: string()
    .nullable()
    .test("", "*Required", (value) => {
      return isPostIssuance || value
    }),
    sum_insured_min: string()
      .nullable()
      .test("", "*Required", function(value) {
        // Check if Sum Insured attribute exists and has selectable values (indicating min/max is required)
        const sumInsuredAttribute = attributes.find(
          (attr) => attr.attribute_data.attribute_name === "Sum Insured"
        );
        const hasSelectableValues = sumInsuredAttribute?.selectable_values?.length > 0;
        const isFlex = JSON.parse(localStorage.getItem("eb_selected_values") || "{}")?.flex;
        
        // Min is required if it's a flex plan with selectable values
        if (!isFlex || !hasSelectableValues) {
          return true; // Not required
        }
        
        // If required, check if value exists and is not empty
        return value && value !== "" && value !== null && value !== undefined;
      })
      .test("", "The minimum value cannot be higher than the maximum value.", function(value) {
        const { parent } = this;
        const maxValue = parent?.sum_insured_max;
        const sumInsured = parent?.sum_insured;
        
        if (!value || !maxValue || value === "" || maxValue === "" || !sumInsured) {
          return true;
        }
        
        return parseInt(value) <= parseInt(maxValue);
      }),
    sum_insured_max: string()
      .nullable()
      .test("", "*Required", function(value) {
        // Check if Sum Insured attribute exists and has selectable values (indicating min/max is required)
        const sumInsuredAttribute = attributes.find(
          (attr) => attr.attribute_data.attribute_name === "Sum Insured"
        );
        const hasSelectableValues = sumInsuredAttribute?.selectable_values?.length > 0;
        const isFlex = JSON.parse(localStorage.getItem("eb_selected_values") || "{}")?.flex;
        
        // Max is required if it's a flex plan with selectable values
        if (!isFlex || !hasSelectableValues) {
          return true; // Not required
        }
        
        // If required, check if value exists and is not empty
        return value && value !== "" && value !== null && value !== undefined;
      }),
    esc_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Co Payment") || value;
      })
      .test("", "Percentage cannot be 100% or greater", function(value) {
        if (!value || value === "") return true;
        
        // Check if value is a percentage
        if (value.includes('%')) {
          const percentageNum = parseFloat(value.replace(/[%,]/g, ''));
          return percentageNum < 100;
        }
        
        return true; // For non-percentage values, no validation needed
      })
      .test("", "Co-payment cannot be greater than sum insured", function(value) {
        if (!value || value === "") return true;
        const isNA = typeof value === 'string' && ["NA", "N/A"].includes(value.trim().toUpperCase());
        if (isNA) return true;
        
        const { parent } = this;
        const sumInsured = parent?.sum_insured;
        
        if (!sumInsured || sumInsured === "") return true;
        
        // Remove commas and convert to numbers for comparison
        const coPaymentNum = parseFloat(value.replace(/[%,]/g, ''));
        const sumInsuredNum = parseFloat(sumInsured.replace(/[%,]/g, ''));
        
        // If co-payment is a percentage, convert it to actual amount
        let coPaymentAmount = coPaymentNum;
        if (value.includes('%')) {
          coPaymentAmount = (coPaymentNum / 100) * sumInsuredNum;
        }
        
        return coPaymentAmount <= sumInsuredNum;
      }),
    parent_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Co Payment") || value;
      })
      .test("", "Percentage cannot be 100% or greater", function(value) {
        if (!value || value === "") return true;
        
        // Check if value is a percentage
        if (value.includes('%')) {
          const percentageNum = parseFloat(value.replace(/[%,]/g, ''));
          return percentageNum < 100;
        }
        
        return true; // For non-percentage values, no validation needed
      })
      .test("", "Co-payment cannot be greater than sum insured", function(value) {
        if (!value || value === "") return true;
        const isNA = typeof value === 'string' && ["NA", "N/A"].includes(value.trim().toUpperCase());
        if (isNA) return true;
        
        const { parent } = this;
        const sumInsured = parent?.sum_insured;
        
        if (!sumInsured || sumInsured === "") return true;
        
        // Remove commas and convert to numbers for comparison
        const coPaymentNum = parseFloat(value.replace(/[%,]/g, ''));
        const sumInsuredNum = parseFloat(sumInsured.replace(/[%,]/g, ''));
        
        // If co-payment is a percentage, convert it to actual amount
        let coPaymentAmount = coPaymentNum;
        if (value.includes('%')) {
          coPaymentAmount = (coPaymentNum / 100) * sumInsuredNum;
        }
        
        return coPaymentAmount <= sumInsuredNum;
      }),
    room_rent_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Room Rent Limit") || value;
      }),
    maternity_delivery_coverage_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Maternity Coverage") || value;
      }),
    maternity_delivery_coverage_limit_normal_max: string()
      .nullable(),
    maternity_delivery_coverage_limit_icu_max: string()
      .nullable(),
    ambulance_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Ambulance Charges") || value;
      }),
    health_checkup_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Health Checkup Coverage") || value;
      }),
    dental_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "Dental Coverage") || value;
      }),
    critical_illness_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "UNANI Coverages") || value;
      }),
    congenital_internal_limit: string()
      .nullable()
      .test("", "", (value) => {
        return (
          attributePresent(
            attributes,
            "Congenital Internal Disease Coverage"
          ) || value
        );
      }),
    opd_limit: string()
      .nullable()
      .test("", "", (value) => {
        return attributePresent(attributes, "OPD Charges") || value;
      }),
    // Add validation for dependent fields
    dependents_covered: object().shape({
      max_number_children: string()
        .nullable()
        .test("","*Required", (value) => {
          return !selectedDependents.includes("Children") || (value && value !== "");
        }),
      children_age_limit: object().shape({
        min: string()
          .nullable()
          .test("", "*Required", function(value) {
            const { parent } = this;
            const maxNumberChildren = parent?.parent?.max_number_children;
            return !selectedDependents.includes("Children") || maxNumberChildren === "N/A" || (value && value !== "");
          })
          .test("", "The minimum age cannot be higher than the maximum age.", function(value) {
            const { parent } = this;
            const maxValue = parent?.max;
            const maxNumberChildren = parent?.parent?.max_number_children;
            if (!selectedDependents.includes("Children") || maxNumberChildren === "N/A" || !value || !maxValue || value === "" || maxValue === "") {
              return true;
            }
            return parseInt(value) <= parseInt(maxValue);
          })
          .test("", "Minimum age and maximum age cannot be the same. Please enter different values.", function(value) {
            const { parent } = this;
            const maxValue = parent?.max;
            const maxNumberChildren = parent?.parent?.max_number_children;
            if (!selectedDependents.includes("Children") || maxNumberChildren === "N/A" || !value || !maxValue || value === "" || maxValue === "") {
              return true;
            }
            return parseInt(value) !== parseInt(maxValue);
          }),
        max: string()
          .nullable()
          .test("", "*Required", function(value) {
            const { parent } = this;
            const maxNumberChildren = parent?.parent?.max_number_children;
            return !selectedDependents.includes("Children") || maxNumberChildren === "N/A" || (value && value !== "");
          }),
      }),
      parental_age_limit: object().shape({
        min: string()
          .nullable()
          .test("", "*Required", (value) => {
            const hasParents = selectedDependents.includes("Parents") || selectedDependents.includes("In-laws");
            return !hasParents || (value && value !== "");
          })
          .test("", "The minimum age cannot be higher than the maximum age.", function(value) {
            const { parent } = this;
            const maxValue = parent?.max;
            const hasParents = selectedDependents.includes("Parents") || selectedDependents.includes("In-laws");
            if (!hasParents || !value || !maxValue || value === "" || maxValue === "") {
              return true;
            }
            return parseInt(value) <= parseInt(maxValue);
          })
          .test("", "Minimum age and maximum age cannot be the same. Please enter different values.", function(value) {
            const { parent } = this;
            const maxValue = parent?.max;
            const hasParents = selectedDependents.includes("Parents") || selectedDependents.includes("In-laws");
            if (!hasParents || !value || !maxValue || value === "" || maxValue === "") {
              return true;
            }
            return parseInt(value) !== parseInt(maxValue);
          }),
        max: string()
          .nullable()
          .test("", "*Required", (value) => {
            const hasParents = selectedDependents.includes("Parents") || selectedDependents.includes("In-laws");
            return !hasParents || (value && value !== "");
          }),
      }),
    }),
  });
  return validations;
};

export const generateGradeInitials = (attributes, grade) => {
  const generateDynamicInit = (key, initials, name) => {
    // Check if this attribute is a dropdown (has selectable values) or text input
    const attributeDef = attributes.find(attr => 
      attr.attribute_data.attribute_name === name
    );
    const isDropdown = attributeDef?.selectable_values?.length > 0;
    
    console.log(`generateDynamicInit - ${name} field type check:`, {
      isDropdown,
      selectable_values: attributeDef?.selectable_values,
      attribute_default: getAttributeValue(name)?.attribute_default,
      attribute_min: getAttributeValue(name)?.attribute_min,
      attribute_max: getAttributeValue(name)?.attribute_max
    });
    
    return {
      ...initials,
      [`${key}_limit`]: isDropdown 
        ? getAttributeValue(name)?.attribute_default  // Keep raw value for dropdown
        : formatIndianNumber(getAttributeValue(name)?.attribute_default), // Format for text input
      [`${key}_min`]: isDropdown 
        ? getAttributeValue(name)?.attribute_min  // Keep raw value for dropdown
        : formatIndianNumber(getAttributeValue(name)?.attribute_min), // Format for text input
      [`${key}_max`]: isDropdown 
        ? getAttributeValue(name)?.attribute_max  // Keep raw value for dropdown
        : formatIndianNumber(getAttributeValue(name)?.attribute_max), // Format for text input
      [`${key}_remarks`]: getAttributeValue(name)?.attribute_remarks,
    };
  };

  const getAttributeValue = (name) => {
    const attributeId = attributes.find(
      (attr) => attr.attribute_data.attribute_name === name
    )?.attribute_product_mapping_id;
    
    const foundAttribute = grade?.grade_attributes_data?.find(
      (attr) => attr.attribute_product_mapping === attributeId
    );
    
    if (name === "Sum Insured") {
      console.log("getAttributeValue - Sum Insured lookup:", {
        name,
        attributeId,
        grade_attributes_data: grade?.grade_attributes_data,
        foundAttribute,
        attribute_default: foundAttribute?.attribute_default,
        attribute_min: foundAttribute?.attribute_min,
        attribute_max: foundAttribute?.attribute_max
      });
    }
    
    
    return foundAttribute;
  };
  const setupDisease = () => {
    const details = getAttributeValue("Disease Wise Sub limit");
    if (details?.attribute_additional_data?.length) {
      return details?.attribute_additional_data.map((disease) => ({
        disease_name: disease["Disease Name"],
        remarks: disease.attribute_remarks,
        disease_limit: disease.attribute_default,
      }));
    }
    return [""];
  };
  const sumInsuredAttribute = getAttributeValue("Sum Insured");
  console.log("generateGradeInitials - Sum Insured attribute:", {
    grade,
    sumInsuredAttribute,
    attribute_default: sumInsuredAttribute?.attribute_default,
    attribute_min: sumInsuredAttribute?.attribute_min,
    attribute_max: sumInsuredAttribute?.attribute_max,
    grade_attributes_data: grade?.grade_attributes_data
  });


  // Check if sum insured is a dropdown (has selectable values) or text input
  const sumInsuredAttributeDef = attributes.find(attr => 
    attr.attribute_data.attribute_name === "Sum Insured"
  );
  const isSumInsuredDropdown = sumInsuredAttributeDef?.selectable_values?.length > 0;
  
  console.log("Sum Insured field type check:", {
    isSumInsuredDropdown,
    selectable_values: sumInsuredAttributeDef?.selectable_values,
    attribute_default: sumInsuredAttribute?.attribute_default,
    attribute_min: sumInsuredAttribute?.attribute_min,
    attribute_max: sumInsuredAttribute?.attribute_max
  });

  const initials = {
    grade_code: grade?.grade_code,
    sum_insured: isSumInsuredDropdown 
      ? sumInsuredAttribute?.attribute_default  // Keep raw value for dropdown
      : formatIndianNumber(sumInsuredAttribute?.attribute_default), // Format for text input
    sum_insured_min: (() => {
      const rawValue = sumInsuredAttribute?.attribute_min;
      const formattedValue = isSumInsuredDropdown 
        ? rawValue  // Keep raw value for dropdown
        : formatIndianNumber(rawValue); // Format for text input
      
      console.log("Sum Insured Min formatting:", {
        isSumInsuredDropdown,
        raw_value: rawValue,
        formatted_value: formattedValue
      });
      return formattedValue;
    })(),
    sum_insured_max: (() => {
      const rawValue = sumInsuredAttribute?.attribute_max;
      const formattedValue = isSumInsuredDropdown 
        ? rawValue  // Keep raw value for dropdown
        : formatIndianNumber(rawValue); // Format for text input
      
      console.log("Sum Insured Max formatting:", {
        isSumInsuredDropdown,
        raw_value: rawValue,
        formatted_value: formattedValue
      });
      return formattedValue;
    })(),
    dependents_covered: {
      max_number_children: grade?.dependents_covered?.max_number_children,
      children_age_limit: {
        min: grade?.dependents_covered?.children_age_limit?.min,
        max: grade?.dependents_covered?.children_age_limit?.max,
      },
      parental_age_limit: {
        min: grade?.dependents_covered?.parental_age_limit?.min,
        max: grade?.dependents_covered?.parental_age_limit?.max,
      },
    },
    esc_limit: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Employee, Spouse and Children"
      ]?.attribute_default;
      // For Co Payment text inputs, preserve the value as-is (including percentage signs)
      if (!isCoPaymentDropdown && rawValue && (rawValue.includes('%') || rawValue.includes(' %'))) {
        return rawValue;
      }
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    esc_limit_min: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Employee, Spouse and Children"
      ]?.attribute_min;
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    esc_limit_max: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Employee, Spouse and Children"
      ]?.attribute_max;
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    parent_limit: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Parents/In-laws"
      ]?.attribute_default;
      // For Co Payment text inputs, preserve the value as-is (including percentage signs)
      if (!isCoPaymentDropdown && rawValue && (rawValue.includes('%') || rawValue.includes(' %'))) {
        return rawValue;
      }
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    parent_limit_min: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Parents/In-laws"
      ]?.attribute_min;
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    parent_limit_max: (() => {
      const coPaymentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Co Payment"
      );
      const isCoPaymentDropdown = coPaymentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Co Payment")?.attribute_additional_data?.[
        "For Parents/In-laws"
      ]?.attribute_max;
      return isCoPaymentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    room_rent_metro_toggler:
      !!getAttributeValue("Room Rent Limit")?.attribute_additional_data[
      "metro"
      ],
    metro_room_rent_limit_normal:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data["metro"]
        ?.normal?.limit),
    metro_room_rent_limit_normal_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data["metro"]
        ?.normal?.max),
    metro_room_rent_limit_icu:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data["metro"]
        ?.icu?.limit),
    metro_room_rent_limit_icu_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data["metro"]
        ?.icu?.max),
    metro_room_rent_remarks:
      getAttributeValue("Room Rent Limit")?.attribute_additional_data["metro"]
        ?.remarks,
    non_metro_room_rent_limit_normal:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "non_metro"
      ]?.normal?.limit),
    non_metro_room_rent_limit_normal_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "non_metro"
      ]?.normal?.max),
    non_metro_room_rent_limit_icu:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "non_metro"
      ]?.icu?.limit),
    non_metro_room_rent_limit_icu_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "non_metro"
      ]?.icu?.max),
    non_metro_room_rent_remarks:
      getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "non_metro"
      ]?.remarks,

    default_metro_room_rent_limit_normal:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "default_metro"
      ]?.normal?.limit),
    default_metro_room_rent_limit_normal_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "default_metro"
      ]?.normal?.max),
    default_metro_room_rent_limit_icu:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "default_metro"
      ]?.icu?.limit),
    default_metro_room_rent_limit_icu_max:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "default_metro"
      ]?.icu?.max),
    default_metro_room_rent_remarks:
      getAttributeValue("Room Rent Limit")?.attribute_additional_data[
        "default_metro"
      ]?.remarks,

    room_rent_limit: (() => {
      const roomRentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Room Rent Limit"
      );
      const isRoomRentDropdown = roomRentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Room Rent Limit")?.attribute_default;
      return isRoomRentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    room_rent_limit_min: (() => {
      const roomRentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Room Rent Limit"
      );
      const isRoomRentDropdown = roomRentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Room Rent Limit")?.attribute_min;
      return isRoomRentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    room_rent_limit_max: (() => {
      const roomRentAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Room Rent Limit"
      );
      const isRoomRentDropdown = roomRentAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Room Rent Limit")?.attribute_max;
      return isRoomRentDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    room_rent_limit_max_normal:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data?.[
      "Max Per Day Limit for Normal Room (optional)"
      ]),
    room_rent_limit_max_icu:
      formatIndianNumber(getAttributeValue("Room Rent Limit")?.attribute_additional_data?.[
      "Max per day limit for ICU (optional)"
      ]),
    room_rent_remarks: getAttributeValue("Room Rent Limit")?.attribute_remarks,

    maternity_metro_toggler:
      !!getAttributeValue("Maternity Coverage")?.attribute_additional_data[
      "metro"
      ],

    metro_maternity_delivery_coverage_limit_normal:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["Delivery Coverage"]?.normal_limit),
    metro_maternity_delivery_coverage_limit_caeserian:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["Delivery Coverage"]?.caeserian_limit),
    metro_maternity_delivery_coverage_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["Delivery Coverage"]?.remarks,

    metro_maternity_new_born_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["New Born Cover"]?.attribute_default),
    metro_maternity_new_born_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["New Born Cover"]?.attribute_remarks,

    metro_maternity_natal_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["Pre/Post Natal Cover"]?.attribute_default),
    metro_maternity_natal_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "metro"
      ]?.["Pre/Post Natal Cover"]?.attribute_remarks,

    non_metro_maternity_delivery_coverage_limit_normal:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["Delivery Coverage"]?.normal_limit),
    non_metro_maternity_delivery_coverage_limit_caeserian:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["Delivery Coverage"]?.caeserian_limit),
    non_metro_maternity_delivery_coverage_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["Delivery Coverage"]?.remarks,

    non_metro_maternity_new_born_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["New Born Cover"]?.attribute_default),
    non_metro_maternity_new_born_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["New Born Cover"]?.attribute_remarks,

    non_metro_maternity_natal_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["Pre/Post Natal Cover"]?.attribute_default),
    non_metro_maternity_natal_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data[
        "non_metro"
      ]?.["Pre/Post Natal Cover"]?.attribute_remarks,

    maternity_delivery_coverage_limit: (() => {
      const maternityAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Maternity Coverage"
      );
      const isMaternityDropdown = maternityAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.attribute_default;
      return isMaternityDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    maternity_delivery_coverage_limit_min: (() => {
      const maternityAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Maternity Coverage"
      );
      const isMaternityDropdown = maternityAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.attribute_min;
      return isMaternityDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    maternity_delivery_coverage_limit_max: (() => {
      const maternityAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Maternity Coverage"
      );
      const isMaternityDropdown = maternityAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.attribute_max;
      return isMaternityDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    maternity_delivery_coverage_limit_normal_max: (() => {
      const maternityAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Maternity Coverage"
      );
      const isMaternityDropdown = maternityAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.["Max Per Day Limit for Normal Room (optional)"];
      return isMaternityDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    maternity_delivery_coverage_limit_icu_max: (() => {
      const maternityAttrDef = attributes.find(attr => 
        attr.attribute_data.attribute_name === "Maternity Coverage"
      );
      const isMaternityDropdown = maternityAttrDef?.selectable_values?.length > 0;
      const rawValue = getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.["Max per day limit for ICU (optional)"];
      return isMaternityDropdown ? rawValue : formatIndianNumber(rawValue);
    })(),
    maternity_delivery_coverage_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Delivery Coverage"
      ]?.attribute_remarks,
    maternity_new_born_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "New Born Cover"
      ]?.attribute_default),
    maternity_new_born_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "New Born Cover"
      ]?.attribute_remarks,
    maternity_natal_cover_limit:
      formatIndianNumber(getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Pre/Post Natal Cover"
      ]?.attribute_default),
    maternity_natal_cover_remarks:
      getAttributeValue("Maternity Coverage")?.attribute_additional_data?.[
        "Pre/Post Natal Cover"
      ]?.attribute_remarks,
    other_details: grade?.other_details?.length > 0 ? grade.other_details : [""],
    disease_wise_limit_detail: setupDisease(),
  };

  return {
    ...initials,
    ...generateDynamicInit("ambulance", initials, "Ambulance Charges"),
    ...generateDynamicInit(
      "health_checkup",
      initials,
      "Health Checkup Coverage"
    ),
    ...generateDynamicInit("dental", initials, "Dental Coverage"),
    ...generateDynamicInit(
      "critical_illness",
      initials,
      "Critical Illness Coverages"
    ),
    ...generateDynamicInit("unani", initials, "UNANI Coverages"),
    ...generateDynamicInit(
      "congenital_internal",
      initials,
      "Congenital Internal Disease Coverage"
    ),
    ...generateDynamicInit(
      "congenital_external",
      initials,
      "Congenital Internal Disease Coverage"
    ),
    ...generateDynamicInit("opd", initials, "OPD Charges"),
  };
};

export const maxChildrenDropdownOptions = (number, allowEmpty) => {
  const options = Array.from({ length: number }, (_, index) => ({
    value: index + 1,
    label: `${index + 1}`,
  }));
  if (allowEmpty) {
    options.unshift({
      value: "N/A",
      label: "N/A",
    });
  }
  return options;
};

export const ageLimitOptions = (number, offset, allowEmpty) => {
  const options = Array.from({ length: number }, (_, index) => ({
    value: index + 1 + offset,
    label: `${index + 1 + offset}`,
  }));
  if (allowEmpty) {
    options.unshift({
      value: "N/A",
      label: "N/A",
    });
  }
  return options;
};

export const saveGrade = async (
  { grade_code, dependents_covered, ...formikValues },
  userplanId,
  selectedDependents,
  attributes,
  existingGrade,
  toggleStatus,
  isFlat
) => {
  // Helper function to process attribute values based on whether they're dropdown or text input
  const processAttributeValue = (attributeName, value) => {
    const attributeDef = attributes.find(attr => 
      attr.attribute_data.attribute_name === attributeName
    );
    const isDropdown = attributeDef?.selectable_values?.length > 0;
    
    // For Co Payment, preserve the value as-is (including percentage signs)
    if (attributeName === "Co Payment" && !isDropdown) {
      return value || "";
    }
    
    return isDropdown 
      ? value  // Keep as-is for dropdown
      : removeCommas(value); // Remove commas for text input
  };
  const grade_data = {
    dependents_covered: {
      // grade_code,
      max_number_children:
        dependents_covered.max_number_children === "N/A"
          ? ""
          : dependents_covered.max_number_children,
      children_age_limit: {
        min: dependents_covered.children_age_limit?.min,
        max: dependents_covered.children_age_limit?.max,
      },
      parental_age_limit: {
        min:
          dependents_covered.parental_age_limit?.min === "N/A"
            ? ""
            : dependents_covered.parental_age_limit?.min,
        max:
          dependents_covered.parental_age_limit?.max === "N/A"
            ? ""
            : dependents_covered.parental_age_limit?.max,
      },
      dependents_allowed: selectedDependents,
    },
    other_details: [""],
  };
  grade_data.grade_code = removeCommas(formikValues.sum_insured);
  const attributes_data = attributes.map((attr) => {
    let attributeDetails = {
      attribute_product_mapping_id: attr.attribute_product_mapping_id,
    };
    const attributeName = attr.attribute_data.attribute_name;
    switch (attributeName) {
        case "Sum Insured":
          console.log("saveGrade - Sum Insured processing:", {
            raw_sum_insured: formikValues.sum_insured,
            raw_sum_insured_min: formikValues.sum_insured_min,
            raw_sum_insured_max: formikValues.sum_insured_max
          });
          
          attributeDetails = {
            ...attributeDetails,
            attribute_default: processAttributeValue("Sum Insured", formikValues.sum_insured),
            attribute_min: processAttributeValue("Sum Insured", formikValues.sum_insured_min),
            attribute_max: processAttributeValue("Sum Insured", formikValues.sum_insured_max),
          };
        break;
      default:
        // No special processing needed for other attributes
        break;
      case "Co Payment":
        console.log("saveGrade - Co Payment processing:", {
          raw_esc_limit: formikValues.esc_limit,
          raw_parent_limit: formikValues.parent_limit
        });
        
        attributeDetails = {
          ...attributeDetails,
          attribute_additional_data: {
            "For Employee, Spouse and Children": {
              attribute_default: processAttributeValue("Co Payment", formikValues.esc_limit),
              attribute_min: processAttributeValue("Co Payment", formikValues.esc_limit_min),
              attribute_max: processAttributeValue("Co Payment", formikValues.esc_limit_max),
            },
            "For Parents/In-laws": {
              attribute_default: processAttributeValue("Co Payment", formikValues.parent_limit),
              attribute_min: processAttributeValue("Co Payment", formikValues.parent_limit_min),
              attribute_max: processAttributeValue("Co Payment", formikValues.parent_limit_max),
            },
          },
        };
        break;
      case "Room Rent Limit":
        console.log("saveGrade - Room Rent Limit processing:", {
          raw_room_rent_limit: formikValues.room_rent_limit,
          raw_room_rent_limit_min: formikValues.room_rent_limit_min,
          raw_room_rent_limit_max: formikValues.room_rent_limit_max,
          processed_default: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit),
          processed_min: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit_min),
          processed_max: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit_max),
          room_rent_metro_toggler: formikValues.room_rent_metro_toggler
        });
        
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit),
          attribute_min: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit_min),
          attribute_max: processAttributeValue("Room Rent Limit", formikValues.room_rent_limit_max),
          attribute_remarks: formikValues.room_rent_remarks,
        };
        if (formikValues.room_rent_metro_toggler) {
          attributeDetails["attribute_additional_data"] = {
            metro: {
              normal: {
                limit:
                  formikValues.metro_room_rent_limit_normal &&
                  removeCommas(formikValues.metro_room_rent_limit_normal),
                max:
                  formikValues.metro_room_rent_limit_normal_max &&
                  removeCommas(formikValues.metro_room_rent_limit_normal_max),
              },
              icu: {
                limit:
                  formikValues.metro_room_rent_limit_icu &&
                  removeCommas(formikValues.metro_room_rent_limit_icu),
                max:
                  formikValues.metro_room_rent_limit_icu_max &&
                  removeCommas(formikValues.metro_room_rent_limit_icu_max),
              },
              remarks: formikValues.metro_room_rent_remarks,
            },
            non_metro: {
              normal: {
                limit:
                  formikValues.non_metro_room_rent_limit_normal &&
                  removeCommas(formikValues.non_metro_room_rent_limit_normal),
                max:
                  formikValues.non_metro_room_rent_limit_normal_max &&
                  removeCommas(
                    formikValues.non_metro_room_rent_limit_normal_max
                  ),
              },
              icu: {
                limit:
                  formikValues.non_metro_room_rent_limit_icu &&
                  removeCommas(formikValues.non_metro_room_rent_limit_icu),
                max:
                  formikValues.non_metro_room_rent_limit_icu_max &&
                  removeCommas(formikValues.non_metro_room_rent_limit_icu_max),
              },
              remarks: formikValues.non_metro_room_rent_remarks,
            },
          };
        } else if (!formikValues.room_rent_metro_toggler || formikValues.default_metro_room_rent_limit_normal || formikValues.default_metro_room_rent_limit_icu_max || formikValues.default_metro_room_rent_limit_normal || formikValues.default_metro_room_rent_limit_normal_max) {
          attributeDetails["attribute_additional_data"] = {
            default_metro: {
              normal: {
                limit:
                  formikValues.default_metro_room_rent_limit_normal &&
                  removeCommas(
                    formikValues.default_metro_room_rent_limit_normal
                  ),
                max:
                  formikValues.default_metro_room_rent_limit_normal_max &&
                  removeCommas(
                    formikValues.default_metro_room_rent_limit_normal_max
                  ),
              },
              icu: {
                limit:
                  formikValues.default_metro_room_rent_limit_icu &&
                  removeCommas(formikValues.default_metro_room_rent_limit_icu),
                max:
                  formikValues.default_metro_room_rent_limit_icu_max &&
                  removeCommas(
                    formikValues.default_metro_room_rent_limit_icu_max
                  ),
              },
              remarks: formikValues.default_metro_room_rent_remarks,
            },
          };
        } else {
          attributeDetails["attribute_additional_data"] = {
            "Max Per Day Limit for Normal Room (optional)":
              formikValues.room_rent_limit_max_normal,
            "Max per day limit for ICU (optional)":
              formikValues.room_rent_limit_max_icu,
          };
        }
        break;
      case "Maternity Coverage":
        attributeDetails = {
          ...attributeDetails,
          attribute_additional_data: {
            "Delivery Coverage": {
              attribute_default: removeCommas(formikValues.maternity_delivery_coverage_limit),
              attribute_min: removeCommas(formikValues.maternity_delivery_coverage_limit_min),
              attribute_max: removeCommas(formikValues.maternity_delivery_coverage_limit_max),
              attribute_remarks:
                formikValues.maternity_delivery_coverage_remarks,
              "Max Per Day Limit for Normal Room (optional)":
                removeCommas(formikValues.maternity_delivery_coverage_limit_normal_max),
              "Max per day limit for ICU (optional)":
                removeCommas(formikValues.maternity_delivery_coverage_limit_icu_max),
            },
            "New Born Cover": {
              attribute_default: removeCommas(formikValues.maternity_new_born_cover_limit),
              attribute_remarks: formikValues.maternity_new_born_cover_remarks,
            },
            "Pre/Post Natal Cover": {
              attribute_default: removeCommas(formikValues.maternity_natal_cover_limit),
              attribute_remarks: formikValues.maternity_natal_cover_remarks,
            },
          },
        };
        if (formikValues["maternity_metro_toggler"]) {
          attributeDetails["attribute_additional_data"] = {
            metro: {
              "Delivery Coverage": {
                normal_limit:
                  formikValues.metro_maternity_delivery_coverage_limit_normal &&
                  removeCommas(
                    formikValues.metro_maternity_delivery_coverage_limit_normal
                  ),
                caeserian_limit:
                  formikValues.metro_maternity_delivery_coverage_limit_caeserian &&
                  removeCommas(
                    formikValues.metro_maternity_delivery_coverage_limit_caeserian
                  ),
                remarks: formikValues.metro_maternity_delivery_coverage_remarks,
              },
              "New Born Cover": {
                attribute_default:
                  formikValues.metro_maternity_new_born_cover_limit &&
                  removeCommas(
                    formikValues.metro_maternity_new_born_cover_limit
                  ),
                attribute_remarks:
                  formikValues.metro_maternity_new_born_cover_remarks,
              },
              "Pre/Post Natal Cover": {
                attribute_default:
                  formikValues.metro_maternity_natal_cover_limit &&
                  removeCommas(formikValues.metro_maternity_natal_cover_limit),
                attribute_remarks:
                  formikValues.metro_maternity_natal_cover_remarks,
              },
            },
            non_metro: {
              "Delivery Coverage": {
                normal_limit:
                  formikValues.non_metro_maternity_delivery_coverage_limit_normal &&
                  removeCommas(
                    formikValues.non_metro_maternity_delivery_coverage_limit_normal
                  ),
                caeserian_limit:
                  formikValues.non_metro_maternity_delivery_coverage_limit_caeserian &&
                  removeCommas(
                    formikValues.non_metro_maternity_delivery_coverage_limit_caeserian
                  ),
                remarks:
                  formikValues.non_metro_maternity_delivery_coverage_remarks,
              },
              "New Born Cover": {
                attribute_default:
                  formikValues.non_metro_maternity_new_born_cover_limit &&
                  removeCommas(
                    formikValues.non_metro_maternity_new_born_cover_limit
                  ),
                attribute_remarks:
                  formikValues.non_metro_maternity_new_born_cover_remarks,
              },
              "Pre/Post Natal Cover": {
                attribute_default:
                  formikValues.non_metro_maternity_natal_cover_limit &&
                  removeCommas(
                    formikValues.non_metro_maternity_natal_cover_limit
                  ),
                attribute_remarks:
                  formikValues.non_metro_maternity_natal_cover_remarks,
              },
            },
          };
        }
        break;
      case "Ambulance Charges":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Ambulance Charges", formikValues.ambulance_limit),
          attribute_min: processAttributeValue("Ambulance Charges", formikValues.ambulance_min),
          attribute_max: processAttributeValue("Ambulance Charges", formikValues.ambulance_max),
          attribute_remarks: formikValues.ambulance_remarks,
        };
        break;
      case "Critical Illness Coverages":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Critical Illness Coverages", formikValues.critical_illness_limit),
          attribute_min: processAttributeValue("Critical Illness Coverages", formikValues.critical_illness_min),
          attribute_max: processAttributeValue("Critical Illness Coverages", formikValues.critical_illness_max),
          attribute_remarks: formikValues.critical_illness_remarks,
        };
        break;
      case "UNANI Coverages":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("UNANI Coverages", formikValues.unani_limit),
          attribute_min: processAttributeValue("UNANI Coverages", formikValues.unani_min),
          attribute_max: processAttributeValue("UNANI Coverages", formikValues.unani_max),
          attribute_remarks: formikValues.unani_remarks,
        };
        break;
      case "Health Checkup Coverage":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Health Checkup Coverage", formikValues.health_checkup_limit),
          attribute_min: processAttributeValue("Health Checkup Coverage", formikValues.health_checkup_min),
          attribute_max: processAttributeValue("Health Checkup Coverage", formikValues.health_checkup_max),
          attribute_remarks: formikValues.health_checkup_remarks,
        };
        break;
      case "Congenital Internal Disease Coverage":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Congenital Internal Disease Coverage", formikValues.congenital_internal_limit),
          attribute_min: processAttributeValue("Congenital Internal Disease Coverage", formikValues.congenital_internal_min),
          attribute_max: processAttributeValue("Congenital Internal Disease Coverage", formikValues.congenital_internal_max),
          attribute_remarks: formikValues.congenital_internal_remarks,
        };
        break;
      case "Congenital External Disease Coverage":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Congenital External Disease Coverage", formikValues.congenital_external_limit),
          attribute_min: processAttributeValue("Congenital External Disease Coverage", formikValues.congenital_external_min),
          attribute_max: processAttributeValue("Congenital External Disease Coverage", formikValues.congenital_external_max),
          attribute_remarks: formikValues.congenital_external_remarks,
        };
        break;
      case "OPD Charges":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("OPD Charges", formikValues.opd_limit),
          attribute_min: processAttributeValue("OPD Charges", formikValues.opd_min),
          attribute_max: processAttributeValue("OPD Charges", formikValues.opd_max),
          attribute_remarks: formikValues.opd_remarks,
        };
        break;
      case "Dental Coverage":
        attributeDetails = {
          ...attributeDetails,
          attribute_default: processAttributeValue("Dental Coverage", formikValues.dental_limit),
          attribute_min: processAttributeValue("Dental Coverage", formikValues.dental_min),
          attribute_max: processAttributeValue("Dental Coverage", formikValues.dental_max),
          attribute_remarks: formikValues.dental_remarks,
        };
        break;
      case "Disease Wise Sub limit":
        attributeDetails = {
          ...attributeDetails,
          attribute_additional_data: formikValues.disease_wise_limit_detail.map(
            (disease) => ({
              "Disease Name": disease.disease_name,
              attribute_default: disease.disease_limit,
              attribute_remarks: disease.remarks,
            })
          ),
        };
        break;
    }
    return attributeDetails;
  });

  // Removing this to avoid removing attributes that are not toggled off
  // const attributesToRemove = Object.keys(toggleStatus).filter(
  //   (attr) => !toggleStatus[attr]
  // );
  const attributesToRemove = []

  function findAttributeIdsByName(attributeNames, attributes) {
    // Array to hold the matching attribute_product_mapping_ids
    const matchingIds = [];

    // Loop through each attribute in the attributes data
    attributes.forEach((attribute) => {
      // Check if the attribute's name is in the provided attributeNames array
      if (attributeNames.includes(attribute.attribute_data.attribute_name)) {
        // If it is, push the attribute_product_mapping_id to the result array
        matchingIds.push(attribute.attribute_product_mapping_id);
      }
    });

    // Return the array of matching attribute_product_mapping_ids
    return matchingIds;
  }

  const ids = findAttributeIdsByName(attributesToRemove, attributes);
  const attributesParsed = attributes_data.filter(
    (attr) => !ids.includes(attr.attribute_product_mapping_id)
  );

  grade_data.attributes_data = attributesParsed;
  grade_data.other_details = formikValues.other_details.filter(
    (details) => details
  );
  const payload = {
    grade_data,
    userplan_id: userplanId,
  };
  if (existingGrade?.plan_grade_mapping_id) {
    payload.grade_data.plan_grade_mapping_id =
      existingGrade?.plan_grade_mapping_id;
  }
  try {
    console.log("saveGrade - Sending payload for", isFlat ? "FLAT" : "GRADED", "plan:", {
      isFlat,
      payload,
      gradeData: payload.grade_data,
      sumInsured: payload.grade_data.attributes_data?.find(attr => 
        attr.attribute_data?.attribute_name === "Sum Insured"
      ),
    });
    
    const { data } = await http.post("/business/save_grade/", payload);
    
     console.log("saveGrade - Received response for", isFlat ? "FLAT" : "GRADED", "plan:", {
       isFlat,
       response: data,
       gradeData: data?.grade_data,
       sumInsured: data?.grade_data?.attributes_data?.find(attr => 
         attr.attribute_data?.attribute_name === "Sum Insured"
       ),
       allAttributes: data?.grade_data?.attributes_data,
       fullResponse: data
     });
    
    // Don't reload the page - let the parent component handle data refresh
    // This will preserve the current grade selection
    return data;
  } catch (err) {
     if (err.response && err.response.data && err.response.data.message) {
    alert("Error: " + err.response.data);
  } else {
    alert("Error: " + err.response.data);
  }
    // alert("ERROR!!",err);
    throw err; // Re-throw the error so calling code can handle it
  }
};

export const order = [
  "Sum Insured",
  "Co Payment",
  "Room Rent Limit",
  "Maternity Coverage",
  "Ambulance Charges",
  "OPD Charges",
  "Health Checkup Coverage",
  "Dental Coverage",
  "Disease Wise Sub-limit",
  "Critical Illness Coverages",
  "UNANI Coverages",
  "Congenital Internal Disease Coverage",
  "Critical External Disease Coverage",
];

export const maternityOrder = {
  "Delivery Coverage": 1,
  "New Born Cover": 2,
  "Pre/Post Natal Cover": 3,
};

export const sortUserPlanIdsByProductTypeName = (userplanIds, data) => {
  const order = {
    health_insurance: 1,
    personal_accident: 2,
    term_life: 3,
  };

  userplanIds.sort((a, b) => {
    const nameA = data.userplans_data[a].product_type.product_type_name;
    const nameB = data.userplans_data[b].product_type.product_type_name;
    return (order[nameA] || 4) - (order[nameB] || 4);
  });

  return userplanIds;
};
